import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  AlertTriangle, 
  Clock, 
  MapPin, 
  User, 
  CheckCircle, 
  XCircle,
  Search,
  Filter,
  Eye,
  MessageSquare,
  Plus,
  Bell
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { alarmsAPI, sitesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const Alarms = () => {
  const [alarms, setAlarms] = useState([]);
  const [sites, setSites] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [selectedAlarm, setSelectedAlarm] = useState(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    siteId: '',
    type: 'intrusion',
    severity: 'medium',
    title: '',
    description: '',
    location: '',
    triggeredBy: ''
  });
  const { toast } = useToast();

  useEffect(() => {
    loadAlarms();
    loadSites();
  }, []);

  const loadAlarms = async () => {
    try {
      const response = await alarmsAPI.getAll();
      setAlarms(response.data);
    } catch (error) {
      console.error('Erreur chargement alarmes:', error);
      setAlarms([]);
    }
  };

  const loadSites = async () => {
    try {
      const response = await sitesAPI.getAll();
      setSites(response.data);
    } catch (error) {
      console.error('Erreur chargement sites:', error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAddAlarm = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await alarmsAPI.create(formData);
      setAlarms([...alarms, response.data]);
      setIsAddDialogOpen(false);
      setFormData({
        siteId: '',
        type: 'intrusion',
        severity: 'medium',
        title: '',
        description: '',
        location: '',
        triggeredBy: ''
      });
      toast({
        title: "Alarme créée",
        description: "Le système d'alarme a été configuré avec succès",
      });
      loadAlarms();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de créer l'alarme",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredAlarms = alarms.filter(alarm => {
    const matchesSearch = alarm.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         alarm.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || alarm.status.toLowerCase() === statusFilter;
    const matchesSeverity = severityFilter === 'all' || alarm.severity.toLowerCase() === severityFilter;
    return matchesSearch && matchesStatus && matchesSeverity;
  });

  const getSeverityColor = (severity) => {
    switch (severity.toLowerCase()) {
      case 'critique': return 'bg-red-500';
      case 'important': return 'bg-orange-500';
      case 'moyen': return 'bg-yellow-500';
      case 'faible': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getSeverityVariant = (severity) => {
    switch (severity.toLowerCase()) {
      case 'critique': return 'destructive';
      case 'important': return 'secondary';
      case 'moyen': return 'outline';
      case 'faible': return 'default';
      default: return 'outline';
    }
  };

  const getStatusColor = (status) => {
    return status === 'Active' ? 'text-red-600' : 'text-green-600';
  };

  const getStatusIcon = (status) => {
    return status === 'Active' ? XCircle : CheckCircle;
  };

  const handleResolveAlarm = (alarmId) => {
    setAlarms(prev => prev.map(alarm => 
      alarm.id === alarmId 
        ? { ...alarm, status: 'Résolue' }
        : alarm
    ));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Système d'alarmes</h2>
          <p className="text-gray-600">Gestion et suivi des alertes de sécurité</p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant="destructive" className="animate-pulse">
            {alarms.filter(a => a.status === 'Active').length} active(s)
          </Badge>
          <Badge variant="outline">
            {alarms.filter(a => a.status === 'Résolue').length} résolue(s)
          </Badge>
          <Button onClick={() => setIsAddDialogOpen(true)} className="flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>Ajouter alarme</span>
          </Button>
        </div>
      </div>

      {/* Add Alarm Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ajouter un système d'alarme</DialogTitle>
            <DialogDescription>
              Configurez une nouvelle alarme de sécurité
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAddAlarm}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="siteId">Site *</Label>
                  <select
                    id="siteId"
                    name="siteId"
                    value={formData.siteId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Sélectionner un site</option>
                    {sites.map(site => (
                      <option key={site.id} value={site.id}>{site.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Type d'alarme *</Label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="intrusion">Intrusion</option>
                    <option value="fire">Incendie</option>
                    <option value="access_denied">Accès refusé</option>
                    <option value="camera_offline">Caméra hors ligne</option>
                    <option value="panic">Bouton panique</option>
                    <option value="technical">Défaut technique</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="severity">Sévérité *</Label>
                <select
                  id="severity"
                  name="severity"
                  value={formData.severity}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="low">Faible</option>
                  <option value="medium">Moyen</option>
                  <option value="high">Important</option>
                  <option value="critical">Critique</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Titre *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Mouvement détecté - Zone restreinte"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Description détaillée de l'alarme..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="location">Emplacement *</Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Zone A, Secteur 3"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="triggeredBy">Déclenchée par</Label>
                  <Input
                    id="triggeredBy"
                    name="triggeredBy"
                    value={formData.triggeredBy}
                    onChange={handleInputChange}
                    placeholder="Capteur PIR-001, Caméra-05..."
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Création...' : 'Créer l\'alarme'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Search and Filters */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Rechercher une alarme..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filtrer par statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="résolue">Résolue</SelectItem>
          </SelectContent>
        </Select>

        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filtrer par sévérité" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les sévérités</SelectItem>
            <SelectItem value="critique">Critique</SelectItem>
            <SelectItem value="important">Important</SelectItem>
            <SelectItem value="moyen">Moyen</SelectItem>
            <SelectItem value="faible">Faible</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Alarms List */}
      <div className="space-y-4">
        {filteredAlarms.map((alarm) => {
          const StatusIcon = getStatusIcon(alarm.status);
          return (
            <Card key={alarm.id} className={`hover:shadow-md transition-shadow duration-200 ${alarm.status === 'Active' ? 'border-l-4 border-l-red-500' : ''}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className={`w-3 h-3 rounded-full mt-2 ${getSeverityColor(alarm.severity)} ${alarm.status === 'Active' ? 'animate-pulse' : ''}`}></div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-semibold text-lg">{alarm.type}</h3>
                        <Badge variant={getSeverityVariant(alarm.severity)}>
                          {alarm.severity}
                        </Badge>
                        <StatusIcon className={`w-5 h-5 ${getStatusColor(alarm.status)}`} />
                      </div>
                      
                      <p className="text-gray-700 mb-3">{alarm.description}</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4" />
                          <span>{alarm.timestamp}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <MapPin className="w-4 h-4" />
                          <span>{alarm.siteName} • {alarm.location}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <User className="w-4 h-4" />
                          <span>{alarm.assignedTo}</span>
                        </div>
                      </div>
                      
                      <div className="mt-3 text-sm">
                        <span className="font-medium">Déclenché par:</span> {alarm.triggeredBy}
                      </div>
                      
                      {alarm.responseTime && (
                        <div className="mt-2 text-sm">
                          <span className="font-medium">Temps de réponse:</span> 
                          <Badge variant="outline" className="ml-2">{alarm.responseTime}</Badge>
                        </div>
                      )}
                      
                      {alarm.actions && alarm.actions.length > 0 && (
                        <div className="mt-3">
                          <span className="text-sm font-medium">Actions effectuées:</span>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {alarm.actions.map((action, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {action}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setSelectedAlarm(alarm)}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Détails
                    </Button>
                    
                    {alarm.status === 'Active' && (
                      <Button 
                        size="sm" 
                        onClick={() => handleResolveAlarm(alarm.id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Résoudre
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Alarm Detail Modal */}
      {selectedAlarm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-3">
                  <AlertTriangle className={`w-6 h-6 ${selectedAlarm.severity === 'Critique' ? 'text-red-600' : 'text-orange-600'}`} />
                  <span>{selectedAlarm.type}</span>
                </CardTitle>
                <Button variant="ghost" onClick={() => setSelectedAlarm(null)}>
                  ×
                </Button>
              </div>
              <CardDescription>
                {selectedAlarm.siteName} • {selectedAlarm.location}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold">Informations générales</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Horodatage:</span>
                      <span className="text-sm">{selectedAlarm.timestamp}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Sévérité:</span>
                      <Badge variant={getSeverityVariant(selectedAlarm.severity)}>
                        {selectedAlarm.severity}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Statut:</span>
                      <Badge variant={selectedAlarm.status === 'Active' ? 'destructive' : 'default'}>
                        {selectedAlarm.status}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Temps de réponse:</span>
                      <span className="text-sm">{selectedAlarm.responseTime}</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-semibold">Affectation</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Assigné à:</span>
                      <span className="text-sm">{selectedAlarm.assignedTo}</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">Déclenché par:</span>
                      <p className="mt-1 text-gray-600">{selectedAlarm.triggeredBy}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold mb-3">Description complète</h3>
                <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                  {selectedAlarm.description}
                </p>
              </div>
              
              {selectedAlarm.actions && selectedAlarm.actions.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">Actions effectuées</h3>
                  <div className="space-y-2">
                    {selectedAlarm.actions.map((action, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>{action}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                <Button variant="outline">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Ajouter commentaire
                </Button>
                {selectedAlarm.status === 'Active' && (
                  <Button 
                    onClick={() => {
                      handleResolveAlarm(selectedAlarm.id);
                      setSelectedAlarm(null);
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Marquer comme résolue
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Alarms;